import { EmptyState } from "@/components/EmptyState";
import { DocumentIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { Link, useRouterState } from "@tanstack/react-router";
import { cn } from "cn";
import { useState } from "react";

import { MECHANICS_PREVIEW } from "../lib/limits.ts";
import { errorMessage } from "../lib/query.ts";
import { analytics } from "../lib/telemetry/analytics.ts";

import { mechanicsQuery } from "./mechanics.ts";

/**
 * La lista de mecánicas, dentro de la memoria (§7.3).
 *
 * No es una tercera pestaña: la memoria de mecánicas está en módulo propio
 * pero anclada a la memoria existente --entra en el índice del turno, se busca
 * con la misma búsqueda--, así que en la interfaz tampoco es una tercera cosa.
 * Va debajo de las notas y SEPARADA de ellas porque son dos memorias
 * distintas: las notas se sobrescriben, las mecánicas se versionan y cada lote
 * apunta a la versión con la que se calculó (§7.3.5). Mezclarlas en una sola
 * lista es justo el fallo que ese documento marca.
 *
 * Aquí solo se elige. El documento se lee y se escribe en el panel central,
 * que es donde cabe.
 *
 * Va ARRIBA del todo y con su propio scroll: las notas las escribe el agente y
 * crecen solas, así que debajo de ellas esto quedaba a varios dedos de rueda.
 */
export function MechanicsList() {
  const [all, setAll] = useState(false);
  const list = useQuery(mechanicsQuery());

  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });

  const mechanics = list.data?.mechanics ?? [];
  const shown = all ? mechanics : mechanics.slice(0, MECHANICS_PREVIEW);
  const rest = mechanics.length - shown.length;

  return (
    /* Ocupa lo que pide su contenido --que ya viene recortado a unas pocas--
       y como mucho la mitad de la columna: de ahí en adelante scrollea ella.
       Repartir a partes proporcionales al contenido no vale, porque las notas
       crecen sin tope y dejaban esto en línea y media. */
    <section className="flex max-h-[50%] shrink-0 flex-col gap-2">
      {/* El titular no scrollea con la lista: es lo que dice en cuál de las
          dos memorias se está mirando. */}
      <h3 className="flex shrink-0 items-baseline justify-between text-xs font-semibold tracking-wide text-muted-foreground uppercase">
        Mecánicas
        {mechanics.length > 0 && <span>{mechanics.length}</span>}
      </h3>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {list.error != null && (
          <p className="text-sm text-muted-foreground">
            {errorMessage(list.error)}
          </p>
        )}

        {!list.isPending && mechanics.length === 0 && (
          <EmptyState icon={DocumentIcon} className="px-0 py-6">
            Todavía no hay ninguna mecánica. Se escriben dictándoselas al
            agente.
          </EmptyState>
        )}

        <ul className="grid gap-1">
          {shown.map((mechanic) => {
            const to = `/memoria/mecanicas/${mechanic.period}/${mechanic.slug}`;

            return (
              <li key={mechanic.id}>
                <Link
                  to="/memoria/mecanicas/$period/$slug"
                  params={{ period: mechanic.period, slug: mechanic.slug }}
                  search={(previous) => previous}
                  onClick={() => {
                    analytics.mechanic.opened({
                      origin: "memory",
                      period: mechanic.period,
                    });
                  }}
                  className={cn(
                    "grid w-full gap-0.5 rounded-md px-2.5 py-2 text-left transition-colors hover:bg-secondary",
                    pathname === to && "bg-secondary",
                  )}
                >
                  <span className="truncate font-mono text-sm">
                    {mechanic.address}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {mechanic.publishedNumber === null
                      ? "sin publicar"
                      : `vigente v${String(mechanic.publishedNumber)}`}
                    {" · "}
                    {String(mechanic.versionCount)}{" "}
                    {mechanic.versionCount === 1 ? "versión" : "versiones"}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>

        {rest > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full"
            onClick={() => {
              analytics.mechanic.listExpanded();
              setAll(true);
            }}
          >
            Ver todas las {mechanics.length}
          </Button>
        )}
      </div>
    </section>
  );
}
